import { ChevronDownIcon } from 'lucide-react'
import type { AgentParserFile, AgentParserFileRole } from '@axilon-apps/domains-ontology-ui-state'
import { Card } from '../../../components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../../components/ui/collapsible'
import { WriteButton } from '../../IngestPage/shared/WriteButton'
import { COMPACT_ACTION, SECTION_LABEL } from '../../IngestPage/shared/compact'
import { changedSections } from '../util/draftChanges'
import { formatWhen } from '../util/formatMeta'
import type { ConfigSubject } from '../util/subject'
import { ChangeNote } from './ChangeNote'

export function DescriptionTab({
  subject,
  description,
  edited,
  canManage,
  editLabel,
  onEdit
}: {
  subject: ConfigSubject
  /** The text to show — the reviewer's override on a draft, else the subject's. */
  description: string
  edited?: boolean
  canManage: boolean
  /** Absent for non-latest versions, and for a saved agent config while a build is in flight. */
  editLabel?: string
  onEdit?: () => void
}) {
  const changed = changedSections(subject.diff).has('description')
  const before = subject.diff?.entries.find((entry) => entry.path === 'description')?.before
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {subject.compareLabel ? (
          <ChangeNote section="Description" changed={changed} compareLabel={subject.compareLabel} />
        ) : (
          <span className="text-11 text-ink-3">
            {subject.parserKind === 'agent'
              ? 'The prompt the extractor runs with.'
              : 'What the file is and how to read it.'}
          </span>
        )}
        {editLabel && onEdit ? (
          <WriteButton
            canManage={canManage}
            size="sm"
            variant="outline"
            className={COMPACT_ACTION}
            onClick={onEdit}
          >
            {editLabel}
          </WriteButton>
        ) : null}
      </div>
      {edited ? (
        <p className="text-11 text-st-warn" data-testid="description-edited-on-draft">
          Edited on this draft — saved with <em>Save as vN</em>.
        </p>
      ) : null}
      {subject.parserKind === 'agent' ? (
        <p className="text-11 text-ink-3" data-testid="description-prompt-note">
          For an agent parser this text is the system prompt, together with the output tables and
          checks.{' '}
          {subject.kind === 'draft' ? (
            'Edits on this draft re-run the trial extraction before it can be saved.'
          ) : (
            <>
              Change it with <em>Edit</em>, which re-reads the example pages with the new text and
              parks a draft for review, or with <em>Edit ▾ → Edit with agent</em>.
            </>
          )}
        </p>
      ) : null}
      <Card className="p-3">
        <p className="text-xs leading-5 break-words whitespace-pre-wrap">{description}</p>
      </Card>
      {changed && typeof before === 'string' && subject.compareLabel ? (
        <Card className="gap-1.5 p-3">
          <p className={SECTION_LABEL}>Was, in {subject.compareLabel}</p>
          <p className="text-xs leading-5 break-words whitespace-pre-wrap text-ink-2 line-through decoration-ink-3/60">
            {before}
          </p>
        </Card>
      ) : null}
      <AgentScripts subject={subject} />
    </div>
  )
}

const ROLE_LABEL: Record<AgentParserFileRole, string> = {
  preprocess: 'Preprocess',
  finalize: 'Finalize',
  helper: 'Helper',
  module: 'Module'
}

const ROLE_TEXT: Record<AgentParserFileRole, string> = {
  preprocess: "Runs before every page's vision call; the vision call reads its image and hints.",
  finalize:
    'Runs once after every page is read and turns the page tables into the output tables. Each row cites one page and keeps its review flag.',
  helper:
    'The extractor may run it to decide whether to re-read a page with a hint or skip it. It never writes rows; the usage below is all the extractor sees.',
  module: 'Imported by the other scripts; never run on its own.'
}

/** Every script an agent parser runs, read-only: the builder writes them and a new build changes them. */
function AgentScripts({ subject }: { subject: ConfigSubject }) {
  const scripts = subject.agentScripts
  const changed = changedSections(subject.diff).has('agentScripts')
  if (!scripts && !(changed && subject.compareLabel)) return null
  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="agent-scripts">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={SECTION_LABEL}>Scripts</span>
        {scripts ? (
          <span className="text-11 text-ink-3">
            Verified {formatWhen(scripts.verifiedAt)}. Changing one takes a new build.
          </span>
        ) : null}
      </div>
      {subject.compareLabel ? (
        <ChangeNote
          section="Scripts"
          changed={changed}
          compareLabel={subject.compareLabel}
          {...(scripts ? {} : { changedText: 'none now' })}
        />
      ) : null}
      {scripts?.scripts.map((script) => (
        <AgentScriptCard key={script.entrypoint} script={script} />
      ))}
    </div>
  )
}

function AgentScriptCard({ script }: { script: AgentParserFile }) {
  return (
    <Collapsible asChild>
      <Card className="gap-0 p-0" data-testid={`agent-script-${script.entrypoint}`}>
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-3 py-2 text-left">
          <span className={SECTION_LABEL}>{ROLE_LABEL[script.role]}</span>
          <span className="flex items-center gap-2 font-mono text-11 text-ink-3">
            {script.entrypoint}
            <ChevronDownIcon className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <p className="border-t border-line px-3 pt-2 text-11 text-ink-3">
            {ROLE_TEXT[script.role]}
          </p>
          {script.usage ? (
            <div className="px-3 pt-2">
              <p className={SECTION_LABEL}>Usage</p>
              <p className="font-mono text-11 leading-5 break-words whitespace-pre-wrap">
                {script.usage}
              </p>
            </div>
          ) : null}
          <pre className="max-h-[32rem] overflow-auto p-3 font-mono text-11 leading-[1.15rem]">
            {script.script}
          </pre>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
